import React from 'react';
import { useSelector } from 'react-redux';
import { useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';

import { groupsRequestJoinAGroup } from '@wix/bi-logger-groups/v2';

import { selectGroupPrivacyStatus } from 'store/selectors';
import { IFeedItem } from 'api/feed/types';

import {
  Info as InfoIcon,
  Pin as PinIcon,
  Price as PriceIcon,
} from '@wix/wix-ui-icons-common/on-stage';
import { IconProps } from '@wix/wix-ui-icons-common';
import { CardContent, type ICardContentProps } from 'wui/CardContent';
import { Typography } from 'wui/Typography';
import { Box } from 'wui/Box';

import { GroupMembershipButton } from '../../GroupMembership';
import { Attachments } from './Attachments';

import { getAriaId } from '../a11y';

import classes from '../FeedItem.scss';

interface IProps extends Partial<ICardContentProps> {
  item: IFeedItem;
  isPinned: boolean;
  isSuggested: boolean;
  isRestrictedByPP: boolean;
}

type Label = {
  icon: React.FC<IconProps>;
  text: string;
};

export function Disclaimer({
  item,
  isPinned,
  isRestrictedByPP,
  isSuggested,
  ...rest
}: IProps) {
  const { t } = useTranslation();
  const { isMobile } = useEnvironment();

  const feedItemId = item.feedItemId as string;
  const groupId = item.applicationContext?.contextId as string;
  const privacyStatus = useSelector(selectGroupPrivacyStatus(groupId));

  const label = getLabel();
  if (!label) {
    return null;
  }

  const { icon: IconComponent, text } = label;

  return (
    <CardContent
      as="div"
      align={isMobile ? undefined : 'space-between'}
      wrap
      gap="SP2"
      direction={isMobile ? 'vertical' : 'horizontal'}
      verticalAlign="middle"
      id={getAriaId(feedItemId, 'disclaimer')}
      {...rest}
    >
      <Box gap="SP2" verticalAlign="middle">
        <IconComponent size={24} className={classes.icon} />
        <Typography className={classes.disclaimerText}>{text}</Typography>
      </Box>
      {getSecondaryAction()}
    </CardContent>
  );

  function getLabel(): Label | null {
    if (isPinned) {
      return {
        icon: PinIcon,
        text: t('groups-web.discussion.feed.pined-post'),
      };
    }

    if (isSuggested) {
      return {
        icon: InfoIcon,
        text: isMobile
          ? t(
              'groups-web.discussion.feed.mobile.suggested-group.post-label.info',
            )
          : t('groups-web.discussion.feed.suggested-group.post-label.info'),
      };
    }

    if (isRestrictedByPP) {
      return {
        icon: PriceIcon,
        text: t('groups-web.discussion.feed.paid-post.disclaimer'),
      };
    }

    return null;
  }

  function getSecondaryAction() {
    if (isRestrictedByPP) {
      return <Attachments stats={item.mediaData} />;
    }

    if (isSuggested) {
      return (
        <Box grow={isMobile}>
          <GroupMembershipButton
            outlined
            redirect
            groupId={groupId}
            bi={groupsRequestJoinAGroup({
              group_id: groupId,
              origin: 'public_feed',
              type: (privacyStatus || '').toLowerCase(),
            })}
            size="tiny"
            fullWidth
          />
        </Box>
      );
    }
    return null;
  }
}

Disclaimer.displayName = 'Disclaimer';
